// Currency utility based on user location

// Country to Currency mapping (expanded)
const COUNTRY_CURRENCY_MAP = {
  'NG': { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  'US': { code: 'USD', symbol: '$', name: 'US Dollar' },
  'GB': { code: 'GBP', symbol: '£', name: 'British Pound' },
  'CA': { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  'AU': { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  'FR': { code: 'EUR', symbol: '€', name: 'Euro' },
  'DE': { code: 'EUR', symbol: '€', name: 'Euro' },
  'IT': { code: 'EUR', symbol: '€', name: 'Euro' },
  'ES': { code: 'EUR', symbol: '€', name: 'Euro' },
  'NL': { code: 'EUR', symbol: '€', name: 'Euro' },
  'BE': { code: 'EUR', symbol: '€', name: 'Euro' },
  'PT': { code: 'EUR', symbol: '€', name: 'Euro' },
  'GR': { code: 'EUR', symbol: '€', name: 'Euro' },
  'IE': { code: 'EUR', symbol: '€', name: 'Euro' },
  'AT': { code: 'EUR', symbol: '€', name: 'Euro' },
  'FI': { code: 'EUR', symbol: '€', name: 'Euro' },
  'ZA': { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  'GH': { code: 'GHS', symbol: '₵', name: 'Ghanaian Cedi' },
  'KE': { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  'AE': { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  'SA': { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  'IN': { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  'CN': { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  'JP': { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  'SG': { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  'EG': { code: 'EGP', symbol: 'E£', name: 'Egyptian Pound' },
  'MX': { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
  'BR': { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
};

// Currency conversion rates (NGN per 1 unit of foreign currency) - indicative only.
// Must match backend flight-search rates so NGN display is consistent.
const CURRENCY_RATES = {
  'NGN': 1,
  'USD': 1450,
  'GBP': 1830,
  'EUR': 1580,
  'CAD': 1050,
  'AUD': 950,
  'ZAR': 78,
  'GHS': 98,
  'KES': 10,
  'AED': 395,
  'SAR': 385,
  'INR': 17,
  'CNY': 200,
  'JPY': 10,
  'SGD': 1070,
  'EGP': 48,
  'MXN': 85,
  'BRL': 290,
};

// Default currency (fallback)
const DEFAULT_CURRENCY = { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' };

// Get user's country from browser locale or IP
const getUserCountry = async () => {
  try {
    console.log('🌍 Starting country detection...');
    
    // Method 1: Try timezone FIRST (most reliable, not blocked by tracking prevention)
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('📍 Browser timezone:', timezone);
    const timezoneCountryMap = {
      'Africa/Lagos': 'NG',
      'Africa/Cairo': 'EG',
      'Africa/Johannesburg': 'ZA',
      'Africa/Accra': 'GH',
      'Africa/Nairobi': 'KE',
      'America/New_York': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'America/Toronto': 'CA',
      'America/Mexico_City': 'MX',
      'America/Sao_Paulo': 'BR',
      'Europe/London': 'GB',
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Europe/Rome': 'IT',
      'Europe/Madrid': 'ES',
      'Europe/Amsterdam': 'NL',
      'Europe/Brussels': 'BE',
      'Europe/Lisbon': 'PT',
      'Europe/Athens': 'GR',
      'Europe/Dublin': 'IE',
      'Europe/Vienna': 'AT',
      'Europe/Helsinki': 'FI',
      'Asia/Dubai': 'AE',
      'Asia/Singapore': 'SG',
      'Asia/Tokyo': 'JP',
      'Asia/Shanghai': 'CN',
      'Asia/Mumbai': 'IN',
      'Australia/Sydney': 'AU',
      'Australia/Melbourne': 'AU',
    };
    
    if (timezoneCountryMap[timezone]) {
      console.log('✅ Country detected from timezone:', timezoneCountryMap[timezone], '(timezone:', timezone + ')');
      return timezoneCountryMap[timezone];
    }
    
    // Method 2: Try browser locale (may be inaccurate if browser is set to en-US)
    const locale = navigator.language || navigator.userLanguage || navigator.languages?.[0] || 'en-US';
    console.log('🌐 Browser locale:', locale);
    
    // Extract country code from locale (e.g., 'en-US' -> 'US', 'en-GB' -> 'GB')
    let countryCode = null;
    if (locale.includes('-')) {
      countryCode = locale.split('-')[1]?.toUpperCase();
    } else if (locale.includes('_')) {
      countryCode = locale.split('_')[1]?.toUpperCase();
    }
    
    // Only use locale if it's NOT en-US (which is often default even outside US)
    if (countryCode && countryCode !== 'US' && COUNTRY_CURRENCY_MAP[countryCode]) {
      console.log('✅ Country detected from browser locale:', countryCode);
      return countryCode;
    }
    
    // If locale is en-US, skip it and try other methods
    if (countryCode === 'US') {
      console.log('⚠️ Browser locale is en-US (often default), trying other methods...');
    }
    
    // Method 3: Try IP-based geolocation - PRIMARY METHOD (most accurate)
    // Try multiple reliable IP geolocation services
    const ipServices = [
      { url: 'https://ipapi.co/json/', getCountry: (data) => data.country_code },
      { url: 'https://api.country.is/', getCountry: (data) => data.country },
      { url: 'https://ip-api.com/json/?fields=countryCode', getCountry: (data) => data.countryCode },
      { url: 'https://api.ipgeolocation.io/ipgeo?apiKey=free', getCountry: (data) => data.country_code2 }
    ];

    for (const service of ipServices) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        console.log(`Attempting IP geolocation via ${service.url}...`);
        const response = await fetch(service.url, { 
          signal: controller.signal,
          mode: 'cors',
          headers: {
            'Accept': 'application/json'
          }
        });
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          console.log('IP geolocation response:', data);
          const countryCode = service.getCountry(data);
          if (countryCode) {
            const detectedCountry = countryCode.toUpperCase();
            if (COUNTRY_CURRENCY_MAP[detectedCountry]) {
              console.log('✅ Country detected from IP:', detectedCountry);
              return detectedCountry;
            }
          }
        }
      } catch (ipError) {
        console.log(`IP geolocation service failed (${service.url}):`, ipError.message);
        continue; // Try next service
      }
    }

    // Method 5: Try using Intl API for locale
    try {
      const localeInfo = new Intl.Locale(navigator.language || 'en-US');
      if (localeInfo.region) {
        const regionCode = localeInfo.region.toUpperCase();
        if (COUNTRY_CURRENCY_MAP[regionCode]) {
          console.log('✅ Country detected from Intl.Locale:', regionCode);
          return regionCode;
        }
      }
    } catch (intlError) {
      console.log('Intl.Locale not supported:', intlError.message);
    }

    // Final fallback: Use browser language if available
    if (countryCode) {
      console.log('⚠️ Using browser locale as fallback:', countryCode);
      return countryCode;
    }

    console.log('⚠️ No country detected, defaulting to Nigeria (NG)');
    return 'NG'; // Default to Nigeria
  } catch (error) {
    console.error('❌ Error detecting user country:', error);
    return 'NG'; // Default to Nigeria
  }
};

// Get currency based on country
const getCurrencyByCountry = (countryCode) => {
  return COUNTRY_CURRENCY_MAP[countryCode] || DEFAULT_CURRENCY;
};

// Initialize currency (call this once on app load)
let userCurrency = DEFAULT_CURRENCY;
let isInitialized = false;
let initializationPromise = null;

export const initializeCurrency = async (forceRefresh = false) => {
  // If force refresh, clear cache and re-detect
  if (forceRefresh) {
    console.log('🔄 Force refreshing currency detection...');
    localStorage.removeItem('userCurrency');
    isInitialized = false;
    userCurrency = DEFAULT_CURRENCY;
    initializationPromise = null;
  }
  
  // If already initialized and not forcing refresh, return cached currency
  if (isInitialized && !forceRefresh) {
    console.log('Using cached currency:', userCurrency);
    return userCurrency;
  }
  
  // If initialization is in progress, wait for it
  if (initializationPromise) {
    return initializationPromise;
  }
  
  // Start initialization
  initializationPromise = (async () => {
    try {
      // Check localStorage first (only if not forcing refresh)
      if (!forceRefresh) {
        const savedCurrency = localStorage.getItem('userCurrency');
        if (savedCurrency) {
          try {
            const parsed = JSON.parse(savedCurrency);
            if (parsed && parsed.code && parsed.symbol) {
              userCurrency = parsed;
              isInitialized = true;
              console.log('✅ Using saved currency from localStorage:', userCurrency);
              return userCurrency;
            }
          } catch (e) {
            console.log('Invalid saved currency, detecting new one');
          }
        }
      }

      // Detect country and get currency
      console.log('🌍 Detecting user location...');
      const countryCode = await getUserCountry();
      console.log('✅ Detected country code:', countryCode);
      
      userCurrency = getCurrencyByCountry(countryCode);
      console.log('✅ Selected currency:', {
        code: userCurrency.code,
        symbol: userCurrency.symbol,
        name: userCurrency.name,
        countryCode: countryCode
      });
      
      // Log detection method used
      console.log('📍 Currency detection complete. Prices will be shown in:', userCurrency.symbol, userCurrency.code);
      
      // Save to localStorage
      localStorage.setItem('userCurrency', JSON.stringify(userCurrency));
      isInitialized = true;
      
      // Dispatch event to notify components
      window.dispatchEvent(new CustomEvent('currencyDetected', { 
        detail: { currency: userCurrency, countryCode } 
      }));
      
      return userCurrency;
    } catch (error) {
      console.error('❌ Error initializing currency:', error);
      userCurrency = DEFAULT_CURRENCY;
      isInitialized = true;
      return userCurrency;
    } finally {
      initializationPromise = null;
    }
  })();
  
  return initializationPromise;
};

// Get current currency
export const getCurrency = () => {
  if (!isInitialized) {
    // Try to get from localStorage
    const savedCurrency = localStorage.getItem('userCurrency');
    if (savedCurrency) {
      try {
        userCurrency = JSON.parse(savedCurrency);
        isInitialized = true;
      } catch (e) {
        // Invalid, use default
      }
    }
  }
  return userCurrency;
};

// Set currency manually (for user preference)
export const setCurrency = (currencyCode) => {
  const currency = Object.values(COUNTRY_CURRENCY_MAP).find(c => c.code === currencyCode) || DEFAULT_CURRENCY;
  userCurrency = currency;
  localStorage.setItem('userCurrency', JSON.stringify(currency));
  return currency;
};

// Format amount with currency symbol
export const formatCurrency = (amount, options = {}) => {
  const currency = options.currency || getCurrency();
  const showSymbol = options.showSymbol !== false;
  const decimals = options.decimals !== undefined ? options.decimals : 2;
  
  if (!amount || isNaN(amount)) {
    return showSymbol ? `${currency.symbol}0` : '0';
  }

  const numAmount = parseFloat(amount);
  
  // Format number with locale-specific formatting
  const formatted = numAmount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return showSymbol ? `${currency.symbol}${formatted}` : formatted;
};

// Convert amount directly from one currency to another (e.g. API GBP → user USD)
export const convertCurrency = (amount, fromCurrency, toCurrency) => {
  if (!amount || isNaN(amount)) return 0;
  if (fromCurrency === toCurrency) return Math.round(parseFloat(amount) * 100) / 100;

  const fromRate = CURRENCY_RATES[fromCurrency] || 1;
  const toRate = CURRENCY_RATES[toCurrency] || 1;
  const convertedAmount = (amount * fromRate) / toRate;

  return Math.round(convertedAmount * 100) / 100; // Round to 2 decimals
};

// Format price (converts NGN to user's currency if needed)
export const formatPrice = (amountNGN, options = {}) => {
  const currency = options.currency || getCurrency();
  
  if (!currency || !currency.code) {
    console.warn('formatPrice: Invalid currency, using default');
    const defaultCurrency = DEFAULT_CURRENCY;
    return formatCurrency(amountNGN, { ...options, currency: defaultCurrency });
  }
  
  // If amount is already 0 or invalid, return formatted 0
  if (!amountNGN || isNaN(amountNGN) || amountNGN <= 0) {
    return formatCurrency(0, { ...options, currency });
  }
  
  if (currency.code === 'NGN') {
    return formatCurrency(amountNGN, { ...options, currency });
  }
  
  // Convert from NGN to user's currency (indicative rate)
  const convertedAmount = convertCurrency(amountNGN, 'NGN', currency.code);
  return formatCurrency(convertedAmount, { ...options, currency });
};

// Get all available currencies
export const getAvailableCurrencies = () => {
  return Object.values(COUNTRY_CURRENCY_MAP);
};

// Get currency info by code (for formatting in a specific currency)
const CURRENCY_BY_CODE = Object.values(COUNTRY_CURRENCY_MAP).reduce((acc, c) => {
  acc[c.code] = c;
  return acc;
}, {});

// Format amount in a specific currency (e.g. API original currency)
export const formatAmountInCurrency = (amount, currencyCode, options = {}) => {
  const currency = CURRENCY_BY_CODE[currencyCode] || { code: currencyCode, symbol: currencyCode === 'USD' ? '$' : currencyCode === 'GBP' ? '£' : currencyCode === 'EUR' ? '€' : currencyCode + ' ', name: currencyCode };
  return formatCurrency(parseFloat(amount) || 0, { ...options, currency });
};

/**
 * Format a single monetary component (base fare, taxes) from a flight Duffel price object.
 * Amounts are in the offer's currency — NOT NGN. Do not use formatPrice() for these.
 */
export const formatFlightPriceComponent = (amount, priceObj, options = {}) => {
  const n = parseFloat(amount);
  if (amount === undefined || amount === null || isNaN(n)) {
    return formatCurrency(0, { ...options, currency: options.currency || getCurrency() });
  }
  const userCurrency = options.currency || getCurrency();
  const originalCurrency = priceObj?.originalCurrency || priceObj?.currency || priceObj?.original_currency;
  if (!originalCurrency) {
    return formatPrice(n, options);
  }
  if (userCurrency.code === originalCurrency) {
    return formatAmountInCurrency(n, originalCurrency, options);
  }
  const converted = convertCurrency(n, originalCurrency, userCurrency.code);
  return formatCurrency(converted, { ...options, currency: userCurrency });
};

/**
 * Format flight price for display.
 * - Converts directly from API currency to user's currency (no NGN step).
 * - When options.perPassenger is true and options.passengerCount > 0,
 *   it will divide the total by the number of passengers so we show price per traveller.
 *
 * priceObj: { total, currency, originalCurrency?, originalTotal? }
 * When originalCurrency and originalTotal exist, converts that amount directly to user's currency and displays it.
 */
export const formatFlightPrice = (priceObj, options = {}) => {
  if (!priceObj) return formatPrice(0, options);
  if (typeof priceObj === 'number') return formatPrice(priceObj, options);
  const userCurrency = options.currency || getCurrency();
  const originalCurrency = priceObj.originalCurrency || priceObj.original_currency;
  const originalTotal = priceObj.originalTotal ?? priceObj.original_total;

  // Helper to optionally convert total into a per-passenger amount before formatting
  const getPerPassengerAmount = (amount) => {
    const count = options.perPassenger && options.passengerCount ? Number(options.passengerCount) : null;
    if (!count || !isFinite(count) || count <= 0) return amount;
    return amount / count;
  };

  if (originalCurrency && (originalTotal !== undefined && originalTotal !== null)) {
    let amount = parseFloat(originalTotal) || 0;
    if (!amount) return formatCurrency(0, { ...options, currency: userCurrency });
    amount = getPerPassengerAmount(amount);
    if (!userCurrency?.code) return formatAmountInCurrency(amount, originalCurrency, options);
    // Debug: always log both currencies so we can verify they match
    console.log('[Currency] Debug currencies', {
      originalCurrency,
      userCurrencyCode: userCurrency.code,
      amountBeforeConversion: amount
    });
    if (userCurrency.code === originalCurrency) {
      console.log('[Currency] Flight price – no conversion: same as API', {
        apiAmount: amount,
        currency: originalCurrency
      });
      return formatAmountInCurrency(amount, originalCurrency, options);
    }
    const converted = convertCurrency(amount, originalCurrency, userCurrency.code);
    const formatted = formatCurrency(converted, { ...options, currency: userCurrency });
    console.log('[Currency] Flight price – converted to user currency', {
      from: { amount, currency: originalCurrency },
      to: { amount: converted, currency: userCurrency.code },
      displayed: formatted
    });
    return formatted;
  }

  const total = priceObj.total !== undefined && priceObj.total !== null ? parseFloat(priceObj.total) : 0;
  const perPassengerTotal = getPerPassengerAmount(total);
  console.log('[Currency] Flight price – using fallback (no API original)', { total: perPassengerTotal, assumedNGN: true });
  return formatPrice(perPassengerTotal, options);
};
